import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ElementType,
  type ForwardedRef,
  type HTMLAttributes,
  type ReactElement,
  type Ref,
} from "react";
import clsx from "clsx";

export type CardPadding = "none" | "sm" | "md" | "lg";
export type CardSurface = 1 | 2 | "raised";

type CardAs = "div" | "button" | "section" | "article" | "a";

type PolymorphicProps<E extends CardAs> = E extends "button"
  ? ButtonHTMLAttributes<HTMLButtonElement>
  : E extends "a"
    ? AnchorHTMLAttributes<HTMLAnchorElement>
    : HTMLAttributes<HTMLElement>;

export type CardProps<E extends CardAs = "div"> = PolymorphicProps<E> & {
  as?: E;
  padding?: CardPadding;
  surface?: CardSurface;
  interactive?: boolean;
};

const surfaceClasses: Record<CardSurface, string> = {
  1: "bg-surface-1 border border-border-default",
  2: "bg-surface-2 border border-border-subtle",
  raised: "bg-surface-raised border border-border-subtle",
};

const paddingClasses: Record<CardPadding, string> = {
  none: "",
  sm: "p-4",
  md: "p-6",
  lg: "p-8",
};

function CardImpl<E extends CardAs = "div">(
  {
    as,
    padding = "md",
    surface = 1,
    interactive = false,
    className,
    ...rest
  }: CardProps<E>,
  ref: ForwardedRef<HTMLElement>,
) {
  const Component = (as ?? "div") as ElementType;
  return (
    <Component
      ref={ref}
      className={clsx(
        "rounded-xl shadow-sm",
        surfaceClasses[surface],
        paddingClasses[padding],
        interactive &&
          "transition-colors hover:border-indigo-500/40 cursor-pointer",
        className,
      )}
      {...rest}
    />
  );
}

export const Card = forwardRef(CardImpl) as <E extends CardAs = "div">(
  props: CardProps<E> & { ref?: Ref<HTMLElement> },
) => ReactElement | null;

export default Card;
